import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Send, Clock, CheckCircle, XCircle, User,
  Mail, Phone, AlertCircle, TrendingUp, RefreshCw, Star
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const LiveChatsPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_chats: 0,
    waiting_chats: 0,
    active_chats: 0,
    closed_chats: 0,
    avg_rating: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  
  useEffect(() => {
    if (!user || user.role === 'client') {
      navigate('/login');
      return;
    }
    
    fetchChats();
    fetchStats();
  }, [filterStatus]);
  
  useEffect(() => {
    if (selectedChat) {
      fetchChatMessages(selectedChat.id);
      
      // Poll for new messages
      pollingIntervalRef.current = setInterval(() => {
        fetchChatMessages(selectedChat.id);
      }, 3000);
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [selectedChat]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const fetchChats = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/live-chat/active${params}`);
      setChats(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المحادثات' : 'Failed to load chats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await api.get('/live-chat/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const fetchChatMessages = async (chatId) => {
    try {
      const response = await api.get(`/live-chat/${chatId}`);
      setMessages(response.data.messages);
      
      // تحديث المحادثة في القائمة
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, unread_count: 0 } 
          : chat
      ));
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedChat) return;
    
    try {
      const response = await api.post(`/live-chat/${selectedChat.id}/employee-message`, {
        message: newMessage,
        attachments: []
      });
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      messageInputRef.current?.focus();
      
      // تشغيل صوت إرسال (اختياري)
      playNotificationSound();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
      console.error(error);
    }
  };
  
  const handleCloseChat = async (chatId) => {
    try {
      await api.put(`/live-chat/${chatId}/close`);
      toast.success(i18n.language === 'ar' ? 'تم إغلاق المحادثة' : 'Chat closed');
      fetchChats();
      setSelectedChat(null);
      setMessages([]);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إغلاق المحادثة' : 'Failed to close chat');
      console.error(error);
    }
  };
  
  const playNotificationSound = () => {
    // يمكن إضافة ملف صوت هنا
    // const audio = new Audio('/notification.mp3');
    // audio.play();
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      waiting: {
        label: i18n.language === 'ar' ? 'في الانتظار' : 'Waiting',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-300'
      },
      active: {
        label: i18n.language === 'ar' ? 'نشطة' : 'Active',
        className: 'bg-green-100 text-green-800 border-green-300'
      },
      closed: {
        label: i18n.language === 'ar' ? 'مغلقة' : 'Closed',
        className: 'bg-gray-100 text-gray-800 border-gray-300'
      }
    };
    
    const config = statusConfig[status] || statusConfig.waiting;
    return <Badge className={config.className}>{config.label}</Badge>;
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8" style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-amber-600" />
                {i18n.language === 'ar' ? 'المحادثات الفورية' : 'Live Chats'}
              </h1>
              <p className="text-gray-600 mt-2">
                {i18n.language === 'ar' 
                  ? 'إدارة والرد على محادثات العملاء الفورية'
                  : 'Manage and respond to customer live chats'
                }
              </p>
            </div>
            <button
              onClick={() => {
                fetchChats();
                fetchStats();
              }}
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {i18n.language === 'ar' ? 'تحديث' : 'Refresh'}
            </button>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'الإجمالي' : 'Total'}</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_chats}</p>
                  </div>
                  <MessageCircle className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'في الانتظار' : 'Waiting'}</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.waiting_chats}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'نشطة' : 'Active'}</p>
                    <p className="text-2xl font-bold text-green-600">{stats.active_chats}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'مغلقة' : 'Closed'}</p>
                    <p className="text-2xl font-bold text-gray-600">{stats.closed_chats}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'التقييم' : 'Rating'}</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.avg_rating.toFixed(1)}</p>
                  </div>
                  <Star className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Filters */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterStatus === 'all'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {i18n.language === 'ar' ? 'الكل' : 'All'}
          </button>
          <button
            onClick={() => setFilterStatus('waiting')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterStatus === 'waiting'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {i18n.language === 'ar' ? 'في الانتظار' : 'Waiting'}
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterStatus === 'active'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {i18n.language === 'ar' ? 'نشطة' : 'Active'}
          </button>
          <button
            onClick={() => setFilterStatus('closed')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterStatus === 'closed'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {i18n.language === 'ar' ? 'مغلقة' : 'Closed'}
          </button>
        </div>
        
        {/* Chat Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chats List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">
                {i18n.language === 'ar' ? 'قائمة المحادثات' : 'Chats List'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">
                    {i18n.language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                  </div>
                ) : chats.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    {i18n.language === 'ar' ? 'لا توجد محادثات' : 'No chats'}
                  </div>
                ) : (
                  chats.map((chat) => (
                    <div
                      key={chat.id}
                      onClick={() => setSelectedChat(chat)}
                      className={`p-4 border-b border-gray-200 cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedChat?.id === chat.id ? 'bg-amber-50 border-l-4 border-l-amber-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white font-bold">
                            {chat.visitor_name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{chat.visitor_name}</p>
                            <p className="text-xs text-gray-500">{chat.chat_number}</p>
                          </div>
                        </div>
                        {chat.unread_count > 0 && (
                          <Badge className="bg-red-500 text-white">{chat.unread_count}</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mb-1">
                        {getStatusBadge(chat.status)}
                        <span className="text-xs text-gray-500">
                          {formatTime(chat.last_message_time || chat.started_at)}
                        </span>
                      </div>
                      
                      {chat.last_message && (
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {chat.last_message}
                        </p>
                      )}
                      
                      {chat.assigned_to_name && (
                        <p className="text-xs text-amber-600 mt-1">
                          {i18n.language === 'ar' ? 'مسندة إلى: ' : 'Assigned to: '}
                          {chat.assigned_to_name}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Chat Window */}
          <Card className="lg:col-span-2">
            {!selectedChat ? (
              <div className="h-[600px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg">
                    {i18n.language === 'ar' 
                      ? 'اختر محادثة للبدء'
                      : 'Select a chat to start'
                    }
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {selectedChat.visitor_name}
                        {getStatusBadge(selectedChat.status)}
                      </CardTitle>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {selectedChat.visitor_email}
                        </span>
                        {selectedChat.visitor_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {selectedChat.visitor_phone}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedChat.status !== 'closed' && (
                      <button
                        onClick={() => handleCloseChat(selectedChat.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        {i18n.language === 'ar' ? 'إغلاق المحادثة' : 'Close Chat'}
                      </button>
                    )}
                  </div>
                </CardHeader>
                
                {/* Messages */}
                <CardContent className="p-0">
                  <div className="h-[400px] overflow-y-auto p-4 space-y-3">
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex ${msg.sender_type === 'employee' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl p-3 ${
                            msg.sender_type === 'employee'
                              ? 'bg-amber-500 text-white'
                              : 'bg-gray-200 text-gray-900'
                          }`}
                        >
                          {msg.sender_type === 'employee' && (
                            <p className="text-xs text-amber-100 mb-1 font-semibold">
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                          <p className={`text-xs mt-1 ${msg.sender_type === 'employee' ? 'text-amber-100' : 'text-gray-600'}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Message Input */}
                  {selectedChat.status !== 'closed' && (
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <input
                          ref={messageInputRef}
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={i18n.language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                          className="flex-1 border border-gray-300 rounded-lg p-3 focus:outline-none focus:border-amber-500"
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim()}
                          className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default LiveChatsPage;
